import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Diamond, TrendingUp, Activity, IndianRupee, Download } from "lucide-react";
import { api } from "@/lib/api";
import { exportToCsv } from "@/lib/csv";
import StatCard from "@/components/StatCard";

const PLATFORMS = [
  { key: "all", label: "All Platforms" },
  { key: "trumeet", label: "Trumeet" },
  { key: "olaparty", label: "Qwick Live" },
];

export default function AdminDashboard() {
  const [scope, setScope] = useState("reseller"); // "reseller" | "user"
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [rows, setRows] = useState([]);
  const [platform, setPlatform] = useState("all");
  const [days, setDays] = useState(7);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rangeActive = Boolean(startDate && endDate);

  const computeRange = () => {
    if (rangeActive) return { start: startDate, end: endDate };
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { start: fmt(start), end: fmt(end) };
  };

  useEffect(() => {
    api.get("/admin/stats").then((s) => setStats(s.data)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setDaily(null);
      setRows([]);
      const q = new URLSearchParams();
      if (platform !== "all") q.set("platform", platform);
      if (rangeActive) { q.set("start_date", startDate); q.set("end_date", endDate); }
      else q.set("days", String(days));
      const dailyUrl = scope === "user" ? "/admin/sales/users/daily" : "/admin/sales/daily";
      const bq = new URLSearchParams();
      if (platform !== "all") bq.set("platform", platform);
      const byUrl = scope === "user" ? "/admin/sales/by-user" : "/admin/sales/by-reseller";
      const win = computeRange();
      bq.set("start_date", win.start);
      bq.set("end_date", win.end);
      const [d, b] = await Promise.all([
        api.get(`${dailyUrl}?${q.toString()}`),
        api.get(`${byUrl}${bq.toString() ? `?${bq.toString()}` : ""}`),
      ]);
      setDaily(d.data);
      setRows(b.data);
    })().catch(() => {});
  }, [scope, platform, days, startDate, endDate, rangeActive]);

  const metricKey = scope === "user" ? "revenue" : "diamonds";
  const maxDaily = useMemo(
    () => Math.max(1, ...(daily?.rows || []).map((r) => r[metricKey] || 0)),
    [daily, metricKey]
  );

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Overview</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mt-1">Control Console</h1>
        <p className="text-zinc-500 mt-2 max-w-2xl">Sales through resellers and direct users — filter by platform and date range.</p>
      </div>

      {/* Scope toggle */}
      <div className="inline-flex rounded-lg border border-zinc-300 overflow-hidden" data-testid="scope-toggle">
        <button onClick={() => setScope("reseller")} data-testid="scope-reseller"
          className={`px-4 py-2 text-sm font-medium transition-colors ${scope === "reseller" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>
          <Users className="w-4 h-4 inline mr-1.5" /> Reseller Sales
        </button>
        <button onClick={() => setScope("user")} data-testid="scope-user"
          className={`px-4 py-2 text-sm font-medium transition-colors ${scope === "user" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>
          <IndianRupee className="w-4 h-4 inline mr-1.5" /> User Sales
        </button>
      </div>

      {scope === "reseller" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Resellers" value={stats?.total_resellers ?? "—"} sub={`${stats?.active_resellers ?? 0} active`} icon={Users} testId="stat-resellers" />
          <StatCard label="Diamonds Allocated" value={(stats?.diamonds_allocated ?? 0).toLocaleString()} sub={`${(stats?.diamonds_outstanding ?? 0).toLocaleString()} outstanding`} icon={Diamond} accent="bg-zinc-900 text-white" testId="stat-allocated" />
          <StatCard label="Total Recharges" value={(stats?.total_recharges ?? 0).toLocaleString()} sub={`${(stats?.success_recharges ?? 0).toLocaleString()} successful`} icon={Activity} testId="stat-recharges" />
          <StatCard label="Success Rate" value={`${(stats?.success_rate ?? 0).toFixed(1)}%`} sub="last 500 records" icon={TrendingUp} testId="stat-success-rate" />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3" data-testid="sales-filters">
        <div className="flex flex-wrap gap-2" data-testid="platform-filter">
          {PLATFORMS.map((p) => (
            <button key={p.key} onClick={() => setPlatform(p.key)} data-testid={`filter-platform-${p.key}`}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${platform === p.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500">Quick:</span>
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => { setStartDate(""); setEndDate(""); setDays(n); }} data-testid={`filter-days-${n}`}
              className={`px-2.5 py-1 rounded text-xs font-medium ${!rangeActive && days === n ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>{n}d</button>
          ))}
          <span className="mx-1 h-4 w-px bg-zinc-200" />
          <span className="text-xs text-zinc-500">From</span>
          <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
          <span className="text-xs text-zinc-500">To</span>
          <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
          {rangeActive && (
            <button onClick={() => { setStartDate(""); setEndDate(""); }} data-testid="filter-clear-dates" className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100">Clear</button>
          )}
        </div>
      </div>

      {/* Daily chart */}
      <div className="bg-white border border-zinc-200 rounded-lg" data-testid="daily-sales-card">
        <div className="flex items-end justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-medium text-zinc-900">Daily {scope === "user" ? "revenue" : "sales"}</h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              {rangeActive ? `${daily?.start_date} → ${daily?.end_date}` : `Across ${daily?.days ?? days} days`} · {daily?.platform === "all" ? "all platforms" : daily?.platform}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total in range</div>
            <div className="font-display text-2xl font-semibold tabular-nums font-mono text-zinc-900" data-testid="daily-total">
              {scope === "user" ? `₹${(daily?.total_revenue ?? 0).toLocaleString()}` : (daily?.total_diamonds ?? 0).toLocaleString()}
            </div>
            <div className="text-xs text-zinc-500 font-mono">
              {scope === "user" ? `${(daily?.total_orders ?? 0).toLocaleString()} orders` : `${(daily?.total_recharges ?? 0).toLocaleString()} recharges`}
            </div>
          </div>
        </div>
        <div className="px-6 pt-6">
          <div className="flex items-end gap-1 h-28">
            {(daily?.rows || []).map((r) => {
              const h = Math.round(((r[metricKey] || 0) / maxDaily) * 100);
              return <div key={r.date} title={`${r.date}: ${(r[metricKey] || 0).toLocaleString()}`} className="flex-1 bg-zinc-200 hover:bg-zinc-900 transition-colors rounded-t-sm" style={{ height: `${Math.max(2, h)}%` }} data-testid={`daily-bar-${r.date}`} />;
            })}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Date</Th>
                <Th className="text-right">{scope === "user" ? "Orders" : "Recharges"}</Th>
                <Th className="text-right">{scope === "user" ? "Revenue (₹)" : "Diamonds"}</Th>
              </tr>
            </thead>
            <tbody>
              {(daily?.rows || []).slice().reverse().map((r) => (
                <tr key={r.date} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`daily-row-${r.date}`}>
                  <Td mono>{r.date}</Td>
                  <Td className="text-right tabular-nums font-mono">{((scope === "user" ? r.orders : r.recharges) ?? 0).toLocaleString()}</Td>
                  <Td className="text-right tabular-nums font-mono font-semibold text-zinc-900">{((scope === "user" ? r.revenue : r.diamonds) ?? 0).toLocaleString()}</Td>
                </tr>
              ))}
              {!daily?.rows?.length && <tr><td colSpan="3" className="px-6 py-12 text-center text-sm text-zinc-400">No sales recorded</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Breakdown table */}
      {scope === "reseller" ? (
        <div className="bg-white border border-zinc-200 rounded-lg" data-testid="reseller-sales-card">
          <div className="p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-display text-lg font-medium text-zinc-900">Sales by reseller</h2>
              <p className="text-sm text-zinc-500 mt-0.5">Sales &amp; allocations for the selected period ({rangeActive ? `${startDate} → ${endDate}` : `last ${days} days`}), ranked by diamonds sold. Click a row for detail.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="reseller-sales-date-filter">
              <span className="text-xs text-zinc-500">From</span>
              <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} data-testid="reseller-sales-start-date" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
              <span className="text-xs text-zinc-500">To</span>
              <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} data-testid="reseller-sales-end-date" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
              {rangeActive && (
                <button onClick={() => { setStartDate(""); setEndDate(""); }} data-testid="reseller-sales-clear-dates" className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100">Clear</button>
              )}
              <button onClick={() => exportToCsv("sales-by-reseller", rows, [
              { header: "Reseller", accessor: (s) => s.name }, { header: "Email", accessor: (s) => s.email },
              { header: "Phone", accessor: (s) => s.phone }, { header: "City", accessor: (s) => s.city }, { header: "State", accessor: (s) => s.state },
              { header: "Recharges", accessor: (s) => s.recharges }, { header: "Diamonds sold", accessor: (s) => s.diamonds_sold },
              { header: "Allocated", accessor: (s) => (rangeActive ? s.allocated_in_range : s.allocated) }, { header: "Balance", accessor: (s) => s.balance },
            ])} data-testid="reseller-sales-export-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-300 text-xs font-medium text-zinc-700 hover:bg-zinc-50">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <Th>Reseller</Th><Th>Phone</Th><Th>Location</Th>
                  <Th className="text-right">Recharges</Th><Th className="text-right">Diamonds sold</Th>
                  <Th className="text-right">Allocated (period)</Th><Th className="text-right">Balance</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="7" className="px-6 py-12 text-center text-sm text-zinc-400">No resellers yet</td></tr>}
                {rows.map((s) => (
                  <tr key={s.reseller_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`reseller-sales-row-${s.reseller_id}`}>
                    <Td>
                      <Link to={`/admin/resellers/${s.reseller_id}`} className="font-medium text-zinc-900 hover:text-[#1F9D8A]">{s.name || "—"}</Link>
                      <div className="text-xs text-zinc-500">{s.email}</div>
                    </Td>
                    <Td mono>{s.phone || "—"}</Td>
                    <Td>{[s.city, s.state].filter(Boolean).join(", ") || "—"}</Td>
                    <Td className="text-right tabular-nums font-mono">{(s.recharges ?? 0).toLocaleString()}</Td>
                    <Td className="text-right tabular-nums font-mono font-semibold text-zinc-900">{(s.diamonds_sold ?? 0).toLocaleString()}</Td>
                    <Td className="text-right tabular-nums font-mono">{(s.allocated_in_range ?? 0).toLocaleString()}</Td>
                    <Td className="text-right tabular-nums font-mono">{(s.balance ?? 0).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg" data-testid="user-sales-card">
          <div className="p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-display text-lg font-medium text-zinc-900">Sales by user</h2>
              <p className="text-sm text-zinc-500 mt-0.5">Paid recharges for the selected period ({rangeActive ? `${startDate} → ${endDate}` : `last ${days} days`}), ranked by revenue.</p>
            </div>
            <button onClick={() => exportToCsv("sales-by-user", rows, [
              { header: "Email", accessor: (u) => u.email }, { header: "Orders", accessor: (u) => u.orders }, { header: "Revenue (INR)", accessor: (u) => u.revenue },
            ])} data-testid="user-sales-export-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-300 text-xs font-medium text-zinc-700 hover:bg-zinc-50">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <Th>User</Th><Th className="text-right">Orders</Th><Th className="text-right">Revenue (₹)</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="3" className="px-6 py-12 text-center text-sm text-zinc-400">No user recharges yet</td></tr>}
                {rows.map((u) => (
                  <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-sales-row-${u.user_id}`}>
                    <Td>{u.email}</Td>
                    <Td className="text-right tabular-nums font-mono">{(u.orders ?? 0).toLocaleString()}</Td>
                    <Td className="text-right tabular-nums font-mono font-semibold text-zinc-900">₹{(u.revenue ?? 0).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3 ${className}`}>{children}</th>
);
const Td = ({ children, className = "", mono }) => (
  <td className={`px-6 py-3 text-sm text-zinc-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);
